import React, { useState } from "react";
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  TableContainer,
  Collapse,
  IconButton,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Grid,
} from "@mui/material";
import { Theme, alpha } from "@mui/material/styles";
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Functions as FunctionsIcon,
} from "@mui/icons-material";
import { getDashboardSummary } from "../../store/modules/transactions/transactionActions";
import { useQuery } from "@tanstack/react-query";
import { formatCurrencyForStats } from "../../utils/common";
import { useSelector } from "react-redux";
import { RootState } from "../../store/reducers";
import { ITransactionSummary } from "../../types/transactionSummary";
import {
  TransactionStatus,
  statusColor,
  transactionStatusLabel,
} from "../../hooks/usePipeline";

interface PipelineSummaryContainerProps {
  /** The Pipeline folder id */
  yearId: number;
}

// Column groups, left to right: closest to closing first
const STATUSES = [
  TransactionStatus.UnderContract,
  TransactionStatus.Active,
  TransactionStatus.Lead,
];

interface Totals {
  units: number;
  volume: number;
}

interface SummaryRow {
  category: string;
  byStatus: Map<TransactionStatus, Totals>;
}

type DisplayRow = SummaryRow & { indent?: boolean; bold?: boolean };

/** Sums the server rows (one per type and status) into one row per status */
const toRow = (category: string, rows: ITransactionSummary[]): SummaryRow => {
  const byStatus = new Map<TransactionStatus, Totals>();
  rows.forEach((row) => {
    if (row.status === undefined) return;
    const totals = byStatus.get(row.status) ?? { units: 0, volume: 0 };
    totals.units += row.totalUnits || 0;
    totals.volume += row.totalVolume || 0;
    byStatus.set(row.status, totals);
  });
  return { category, byStatus };
};

/** One row per type, in the order the server returned them */
const typeRows = (rows: ITransactionSummary[]) =>
  Array.from(new Set(rows.map((row) => row.type || "Unknown"))).map((type) =>
    toRow(
      type,
      rows.filter((row) => (row.type || "Unknown") === type),
    ),
  );

const formatCurrency = (value: number) => formatCurrencyForStats(value);
const averagePrice = (totals?: Totals) =>
  totals && totals.units > 0 ? totals.volume / totals.units : 0;

const StatusDot = ({ status }: { status: TransactionStatus }) => {
  const theme = useTheme();
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        width: 10,
        height: 10,
        borderRadius: "50%",
        mr: 0.75,
        bgcolor: statusColor(theme, status),
      }}
    />
  );
};

/** All statuses together, for the Total Pipeline group */
const pipelineTotals = (row: SummaryRow): Totals =>
  STATUSES.reduce(
    (sum, status) => {
      const totals = row.byStatus.get(status);
      return {
        units: sum.units + (totals?.units ?? 0),
        volume: sum.volume + (totals?.volume ?? 0),
      };
    },
    { units: 0, volume: 0 },
  );

interface Group {
  key: string;
  label: React.ReactNode;
  totals: (row: SummaryRow) => Totals | undefined;
  /** Tinted background and a stronger rule above, to set the Total Pipeline group apart */
  shaded?: boolean;
}

/** Row groups, top to bottom: each status, closest to closing first, then all of them together */
const GROUPS: Group[] = [
  ...STATUSES.map((status) => ({
    key: `${status}`,
    label: (
      <>
        <StatusDot status={status} />
        {transactionStatusLabel(status)}
      </>
    ),
    totals: (row: SummaryRow) => row.byStatus.get(status),
  })),
  {
    key: "total",
    label: (
      <>
        <FunctionsIcon sx={{ fontSize: 16, mr: 0.5, color: "text.secondary" }} />
        Total Pipeline
      </>
    ),
    totals: pipelineTotals,
    shaded: true,
  },
];

// Total Pipeline band: a soft slate tint, a little bluer where it crosses the Total column
const shadedBg = (theme: Theme) => alpha(theme.palette.grey[500], 0.08);
const shadedTotalBg = (theme: Theme) => alpha(theme.palette.primary.main, 0.1);
const shadedRule = (theme: Theme) => theme.palette.grey[400];

// Small uppercase labels: the numbers below carry the weight
const headerCell = {
  fontWeight: 600,
  fontSize: "0.75rem",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "text.secondary",
  whiteSpace: "nowrap",
  borderBottom: 1,
  borderBottomColor: "divider",
} as const;

/** The rows under each group, top to bottom */
const METRICS: { label: string; value: (totals?: Totals) => React.ReactNode }[] = [
  { label: "Units", value: (totals) => totals?.units ?? 0 },
  { label: "Volume", value: (totals) => formatCurrency(totals?.volume ?? 0) },
  { label: "Avg Price", value: (totals) => formatCurrency(averagePrice(totals)) },
];

/** Mobile: one card per group, its categories listed */
const MobileByStatusView = ({ rows }: { rows: DisplayRow[] }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
    {GROUPS.map((group) => (
      <Card
        key={group.key}
        variant="outlined"
        sx={{
          borderRadius: 2,
          ...(group.shaded && {
            backgroundColor: shadedBg,
            borderTop: 2,
            borderTopColor: shadedRule,
          }),
        }}
      >
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{ mb: 1, display: "flex", alignItems: "center" }}
          >
            {group.label}
          </Typography>
          {rows.map((row) => {
            const totals = group.totals(row);
            return (
              <Grid container spacing={1} key={row.category} sx={{ mb: 0.5 }}>
                <Grid item xs={5}>
                  <Typography
                    variant="caption"
                    color={row.bold ? "text.primary" : "text.secondary"}
                    sx={{ pl: row.indent ? 1.5 : 0, fontWeight: row.bold ? 600 : undefined }}
                  >
                    {row.category}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography
                    variant="body2"
                    fontWeight={row.bold || group.shaded ? 600 : undefined}
                  >
                    {totals?.units ?? 0}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography
                    variant="body2"
                    fontWeight={row.bold || group.shaded ? 600 : undefined}
                  >
                    {formatCurrency(totals?.volume ?? 0)}
                  </Typography>
                </Grid>
              </Grid>
            );
          })}
        </CardContent>
      </Card>
    ))}
  </Box>
);

/** Desktop: groups as row groups (Units, Volume, Avg Price), categories as columns */
const DesktopByStatusView = ({ rows }: { rows: DisplayRow[] }) => (
  <TableContainer
    component={Paper}
    variant="outlined"
    sx={{ overflowX: "auto", borderRadius: 2, borderColor: "divider" }}
  >
    <Table size="small">
      <TableHead>
        <TableRow sx={{ backgroundColor: "grey.50" }}>
          <TableCell sx={{ ...headerCell, minWidth: 150 }}>Status</TableCell>
          <TableCell sx={headerCell} />
          {rows.map((row) => (
            <TableCell
              key={row.category}
              align="right"
              sx={{
                ...headerCell,
                whiteSpace: "normal",
                minWidth: 100,
                backgroundColor: row.bold ? "primary.50" : undefined,
                // S&B types sit under the S&B column: a step quieter
                ...(row.indent && {
                  fontWeight: 500,
                  fontSize: "0.7rem",
                }),
              }}
            >
              {row.category}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {GROUPS.map((group, groupIndex) => {
          const lastGroup = groupIndex === GROUPS.length - 1;
          // A thicker line closes each group; a darker one sits above the Total Pipeline band, and
          // the last group needs none (the table's edge closes it)
          const groupEndBorder = lastGroup
            ? { borderBottom: 0 }
            : {
                borderBottom: 2,
                borderBottomColor: GROUPS[groupIndex + 1].shaded
                  ? shadedRule
                  : "divider",
              };
          return METRICS.map((metric, index) => {
            const groupEnd = index === METRICS.length - 1;
            const cellSx = {
              py: 1.25,
              ...(groupEnd && groupEndBorder),
            };
            return (
              <TableRow
                key={`${group.key}-${metric.label}`}
                sx={{ backgroundColor: group.shaded ? shadedBg : undefined }}
              >
                {index === 0 && (
                  <TableCell
                    rowSpan={METRICS.length}
                    sx={{
                      fontWeight: group.shaded ? 700 : 600,
                      whiteSpace: "nowrap",
                      verticalAlign: "top",
                      ...groupEndBorder,
                    }}
                  >
                    <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
                      {group.label}
                    </Box>
                  </TableCell>
                )}
                <TableCell sx={{ ...cellSx, color: "text.secondary", whiteSpace: "nowrap" }}>
                  {metric.label}
                </TableCell>
                {rows.map((row) => (
                  <TableCell
                    key={row.category}
                    align="right"
                    sx={{
                      ...cellSx,
                      fontWeight: row.bold || group.shaded ? 600 : undefined,
                      backgroundColor: row.bold
                        ? group.shaded
                          ? shadedTotalBg
                          : "primary.50"
                        : undefined,
                    }}
                  >
                    {metric.value(group.totals(row))}
                  </TableCell>
                ))}
              </TableRow>
            );
          });
        })}
      </TableBody>
    </Table>
  </TableContainer>
);

/** Pipeline folder summary per status and category */
const PipelineSummaryContainer: React.FC<PipelineSummaryContainerProps> = ({
  yearId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [isExpanded, setIsExpanded] = useState(false);

  const { selectedCoAgentIds } = useSelector(
    (state: RootState) => state.coAgentFilter,
  );

  const { data: summaries = [] } = useQuery({
    queryKey: ["dashboardSummary", yearId, selectedCoAgentIds],
    queryFn: () => getDashboardSummary(yearId),
    refetchOnWindowFocus: false,
  });

  if (summaries.length === 0) {
    return null;
  }

  const sbSummaries = summaries.filter((summary) => summary.isSb === true);
  const otherSummaries = summaries.filter((summary) => summary.isSb !== true);

  const rows = [
    { ...toRow("Total", summaries), bold: true },
    ...(sbSummaries.length > 0
      ? [toRow("S&B (Sellers and Buyers)", sbSummaries)]
      : []),
    ...typeRows(sbSummaries).map((row) => ({ ...row, indent: true })),
    ...typeRows(otherSummaries),
  ];

  return (
    <Paper elevation={2} sx={{ mb: 3 }}>
      <Box
        onClick={() => setIsExpanded(!isExpanded)}
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          "&:hover": { backgroundColor: "action.hover" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton size="small" sx={{ mr: 1 }}>
            {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
          </IconButton>
          <Typography
            variant="h6"
            component="h3"
            sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
          >
            Pipeline Summary
          </Typography>
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontStyle: "italic",
            opacity: 0.7,
            display: { xs: "none", sm: "block" },
          }}
        >
          Click to {isExpanded ? "collapse" : "expand"}
        </Typography>
      </Box>

      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {isMobile ? (
            <MobileByStatusView rows={rows} />
          ) : (
            <DesktopByStatusView rows={rows} />
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default PipelineSummaryContainer;
